<script lang="ts" setup>
  import {
    ContentBlockTableData,
    ContentBlockTypeId,
  } from '~/model/ContentBlockData/ContentBlockData'
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useBem from '~/composables/Bem/Bem'
  import {
    TableCellData,
    TableColumnData,
    TableRowData,
  } from '~/model/ContentBlockTable/ContentBlockTable'
  import useMarkdown from '~/composables/Markdown/Markdown'
  import useContentBlockAlign from '~/composables/ContentBlockAlign/ContentBlockAlign'
  import { ContentBlockTableFacet } from '~/components/ContentBlock/ContentBlockTable/ContentBlockTableFacet'

  interface Props extends UseBemProps, ContentBlockTableData {
    type: ContentBlockTypeId
    facets?: Array<string>
    title?: string
    columnTitleList?: Array<TableColumnData>
    rowContentList: Array<TableRowData>
    fixedColWidth?: boolean
  }

  const SAME_VALUE_SIGN = `"`

  const props = withDefaults(defineProps<Props>(), {
    facets: () => [],
    title: '',
    columnTitleList: () => [],
    fixedColWidth: false,
  })
  const { bemFacets, bemAdd } = useBem('c-content-block-table', props, {})
  const { parseMarkdown } = useMarkdown()
  const { contentBlockAlignFacets } = useContentBlockAlign(props)

  const hasColumnTitles = computed<boolean>(() => {
    const hasEntries = Array.isArray(props.columnTitleList) && props.columnTitleList.length > 0
    const hasValues = hasEntries ? props.columnTitleList.some(({ title }) => !!title) : false

    return hasEntries && hasValues
  })
  const hasRowTitles = computed<boolean>(() => {
    return props.rowContentList.some(({ title }) => !!title)
  })
  const numColumns = computed<number>(() => {
    return props.rowContentList.reduce((acc, { columnCellList }) => {
      return Math.max(acc, columnCellList.length)
    }, 0)
  })

  const calculateColspan = (targetIdx: number, cellList: Array<TableCellData>) => {
    return cellList.reduce((acc, cell, idx) => {
      if (idx <= targetIdx) {
        return acc
      }

      return cell.richText === SAME_VALUE_SIGN ? acc + 1 : acc
    }, 1)
  }

  const adaptCells = (cellList: Array<TableCellData>) => {
    return cellList
      .map((cell, idx) => {
        return {
          ...cell,
          colspan: calculateColspan(idx, cellList),
        }
      })
      .filter(({ richText }) => {
        return richText !== SAME_VALUE_SIGN
      })
  }

  const rootClasses = computed<Array<string>>(() => {
    return [
      ...bemFacets.value,
      ...contentBlockAlignFacets.value,
      bemAdd(hasRowTitles ? 'has-row-titles' : 'no-row-titles'),
      bemAdd(props.fixedColWidth ? ContentBlockTableFacet.Fixed : ''),
    ]
  })
</script>

<template>
  <div :class="rootClasses" class="c-content-block-table">
    <div class="c-content-block-table__box">
      <div v-if="title" class="c-content-block-table__header">
        <h2>
          {{ title }}
        </h2>
      </div>
      <div class="c-content-block-table__content">
        <table class="c-content-block-table__table">
          <colgroup>
            <col v-for="n in numColumns + (hasRowTitles ? 1 : 0)" :key="n" />
          </colgroup>

          <thead>
            <tr v-if="hasColumnTitles">
              <th v-for="(columnTitle, columnTitleIdx) in columnTitleList" :key="columnTitleIdx">
                <h3 class="u-reset">
                  {{ columnTitle.title }}
                </h3>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="({ title, columnCellList }, rowContentItemIdx) in rowContentList"
              :key="rowContentItemIdx"
            >
              <th v-if="title">
                <h3 class="u-reset">
                  {{ title }}
                </h3>
              </th>
              <td
                v-for="({ colspan, richText }, columnCellItemIdx) in adaptCells(columnCellList)"
                :key="columnCellItemIdx"
                :colspan="colspan <= 1 ? null : colspan"
                class="u-markdown-root c-content-block-table__content"
                v-html="parseMarkdown(`${richText}`)"
              />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;
  @use '/assets/scss/base/font/font' as font;

  $offset: 4px;

  .c-content-block-table {
    overflow: hidden;
  }

  .c-content-block-table--fixed {
    .c-content-block-table__table {
      table-layout: fixed;
    }

    colgroup col {
      width: 50%;
      overflow: hidden;
      padding: 0;

      &:nth-child(1) {
        width: 200px;
      }
    }
  }

  .c-content-block-table__box {
    @include anaglyph.with-shadow-apply(mono, $offset, $offset, 0, 0, col.$monochrome-lead);
    background-color: col.$variant-cyan-light;
    padding: 40px;
    margin: 0 $offset $offset 0;
  }

  .c-content-block-table__header {
    padding-bottom: 8px;
    border-bottom: 2px solid col.$monochrome-black;
    margin-bottom: 12px;
  }

  .c-content-block-table__table {
    width: 100%;
    border-collapse: collapse;

    // All cells
    thead th,
    tbody th,
    tbody td {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background-color: col.$monochrome-black;
      }
    }

    // First column title cell and row title cells
    tbody tr > th {
      text-align: right;
      min-width: 200px;
    }

    // Column title cells
    thead th {
      vertical-align: middle;
      padding: 8px 16px;
    }

    // Row title cells
    tbody th {
      vertical-align: top;
      text-align: left;
      padding: 12px 16px;
    }

    // Ordinary cells
    tbody td {
      vertical-align: top;
      padding: 12px 16px;
    }

    // All titles
    thead th,
    tbody th {
      h3 {
        @include font.base-bold;
      }
    }

    // Crop lines between table rows
    thead tr,
    tbody tr {
      > :first-child::after {
        left: 16px;
        right: 0;
      }

      > :last-child::after {
        left: 0;
        right: 16px;
      }
    }

    // Alternating table background
    thead {
      &:empty + tbody tr {
        &:nth-child(2n + 0) {
          background-color: rgba(col.$brand-cyan, 0.1);
        }
      }

      &:not(:empty) + tbody tr {
        &:nth-child(2n + 1) {
          background-color: rgba(col.$brand-cyan, 0.1);
        }
      }
    }
  }
</style>
